'use client'
import { FC } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { CreateRequestInput, useSignUpMutation } from '@/app/graphql/generated'
import { getColorBtnSite, getColorSite } from '@/app/shared/components/modals/modal/services'
import { SelectPhoneComponent } from '@/app/shared/components/select-phone'
import useTailwindColor from '@/app/shared/hooks/useTailwindColor'
import { validation } from '@/app/shared/services/validation'
import useLayout, { ITypeSite } from '@/app/shared/store/layout.store'
import { buildUtmTags } from '@/app/shared/lib/attribution'
import { trackApplicationSend } from '@/app/shared/lib/analytics'
import { useMarkersStore } from '@/app/shared/store/markers.store'
import useModal from '@/app/shared/store/modals.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { InputComponent } from '@/app/shared/ui/input'
import { LoadingComponent } from '@/app/shared/ui/loading'
import { SelectComponent } from '@/app/shared/ui/select'
import { TitleComponent } from '@/app/shared/ui/title'
import { customToast } from '@/core/lib/react-toast'

import { ContentComponent } from '../content'

// interface
interface ISignUpLessonModal {
  onClose: () => void
  title?: string
  typeSite: ITypeSite | null
}

interface IForm {
  name: string
  phone: string
  affiliate: string
}

// component
const SignUpLessonComponent: FC<Readonly<ISignUpLessonModal>> = ({ onClose, title, typeSite }) => {
  const { open } = useModal((state) => state)

  const affiliates =
    useLayout((state) => state.affiliates)?.map((el) => ({
      value: el?.id ?? '',
      label: el?.metro ?? '',
    })) ?? []
  const landing = useLayout((state) => state.landing)
  const markers = useMarkersStore((state) => state.markers)

  const { getColor } = useTailwindColor()

  const isOnline = typeSite !== 'offline'

  const currentTitle = title || 'Запишіться на безкоштовний урок'

  const methods = useForm<IForm>({
    defaultValues: {
      name: '',
      phone: undefined,
      affiliate: undefined,
    },
  })

  const [mutate, { loading }] = useSignUpMutation({
    onCompleted: () => {
      // customToast(data.createRequest?.message ?? '', 'success')

      onClose()

      setTimeout(() => {
        open({
          component: () => (
            <ContentComponent
              title='Дякуємо!'
              content='Заявку отримано — наш менеджер незабаром з вами зв’яжеться.'
            />
          ),
        })
      }, 500)

      trackApplicationSend()
    },
  })

  const handleSubmitForm = async (data: IForm) => {

    if (!landing) return

    const obj: CreateRequestInput = {
      full_name: data.name,
      phone: data.phone.replace(/\D/g, ''),
      landing_id: +landing.id,
      is_lessons: true,
    }

    if (!isOnline) obj.affiliates_id = +data.affiliate

    await mutate({
      variables: {
        input: {
          ...obj,
          utm_tags: JSON.stringify(buildUtmTags(markers)),
        },
      },
    })
  }

  // return
  return (
    <div className={'overflow-visible'}>
      <TitleComponent
        classSize={'text-[28px] extraMobile:text-[34px]'}
        className={
          'max-w mb-[30px] w-[90%] text-[22px] extraMobile:text-[26px] 2xl:mb-[60px] [&:first-letter]:uppercase'
        }
      >
        {currentTitle}
      </TitleComponent>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmitForm)} className={'grid gap-[50px]'}>
          <div className={'grid gap-[40px]'}>
            <Controller
              name={'name'}
              control={methods.control}
              rules={{
                required: validation().required,
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <InputComponent
                  onChange={onChange}
                  value={value}
                  placeholder={"Ваше ім'я"}
                  colorType={getColorBtnSite(typeSite)}
                  isInvalid={Boolean(error)}
                  errorMessage={error?.message}
                />
              )}
            />
            <SelectPhoneComponent
              name={'phone'}
              className={'mb-3'}
              isRequired
              colorType={getColorSite(typeSite, getColor)}
            />
            {!isOnline && (
              <Controller
                name={'affiliate'}
                control={methods.control}
                rules={{ required: validation().required }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <SelectComponent
                    placeholder={'Зручний філіал'}
                    className={'mb-3'}
                    aria-label={'affiliate'}
                    option={affiliates}
                    isInvalid={Boolean(error)}
                    errorMessage={error?.message}
                    onChange={onChange}
                    value={value}
                    colorType={getColorBtnSite(typeSite)}
                  />
                )}
              />
            )}
          </div>
          <ButtonComponent
            type={'submit'}
            color={getColorBtnSite(typeSite)}
            className={'mx-auto w-fit'}
            size={'md'}
            isLoading={loading}
          >
            записатись
          </ButtonComponent>
        </form>
        <LoadingComponent isShow={loading} />
      </FormProvider>
    </div>
  )
}

SignUpLessonComponent.displayName = 'SignUpLessonComponent'

export default SignUpLessonComponent
